import React, { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform, useSpring } from "framer-motion";

/**
 * A single scroll-driven "3D moment" between the Hero and the Trust Strip.
 * A large brand mark rotates in three-quarter perspective while a paragraph
 * emerges alongside — a subtle piece of theatre without hurting performance.
 */
export default function HeroSpotlight() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  const smoothOpts = { stiffness: 70, damping: 22, mass: 0.6 };
  const rotateX = useSpring(useTransform(scrollYProgress, [0, 0.6, 1], [45, 0, -30]), smoothOpts);
  const rotateY = useSpring(useTransform(scrollYProgress, [0, 1], [-18, 18]), smoothOpts);
  const scale = useSpring(useTransform(scrollYProgress, [0, 0.5, 1], [0.85, 1.02, 0.94]), smoothOpts);
  const glow = useTransform(scrollYProgress, [0, 0.5, 1], [0.2, 0.55, 0.25]);
  const textY = useTransform(scrollYProgress, [0, 1], [60, -60]);

  return (
    <section
      ref={ref}
      className="relative bg-[#0E1729] overflow-hidden"
      aria-label="Design ethos"
      data-testid="hero-spotlight"
    >
      {/* Ambient gilded halo */}
      <motion.div
        aria-hidden="true"
        style={reduce ? undefined : { opacity: glow }}
        className="absolute inset-0 pointer-events-none bg-[radial-gradient(60%_50%_at_65%_50%,rgba(200,169,106,0.35),transparent_60%)]"
      />
      {/* Grid whisper */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.06] [background-image:linear-gradient(#F7F4EF_1px,transparent_1px),linear-gradient(90deg,#F7F4EF_1px,transparent_1px)] [background-size:60px_60px]"
      />

      <div className="container-rnv relative z-10 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
        {/* 3D brand mark */}
        <div
          className="lg:col-span-6 flex justify-center"
          style={{ perspective: "1400px" }}
        >
          <motion.div
            style={
              reduce
                ? undefined
                : {
                    rotateX,
                    rotateY,
                    scale,
                    transformStyle: "preserve-3d",
                  }
            }
            className="relative w-[260px] h-[340px] md:w-[360px] md:h-[460px]"
            data-testid="hero-spotlight-mark"
          >
            {/* Layered gilded plaques for depth */}
            <div
              className="absolute inset-0 rounded-sm border border-[#C8A96A]/40"
              style={{ transform: "translateZ(-70px)" }}
            />
            <div
              className="absolute inset-4 rounded-sm border border-[#C8A96A]/30"
              style={{ transform: "translateZ(-30px)" }}
            />
            <div
              className="absolute inset-0 rounded-sm bg-gradient-to-br from-[#1E2A44] via-[#111C32] to-[#0B1427] shadow-[0_50px_140px_-30px_rgba(200,169,106,0.35)] border border-[#C8A96A]/30"
              style={{ transform: "translateZ(0px)" }}
            >
              <svg
                viewBox="0 0 200 260"
                className="absolute inset-0 w-full h-full"
                aria-hidden="true"
              >
                <defs>
                  <linearGradient id="rnv-mono" x1="0" x2="1" y1="0" y2="1">
                    <stop offset="0" stopColor="#D8BE87" />
                    <stop offset="1" stopColor="#8E7645" />
                  </linearGradient>
                </defs>
                {/* helix motif */}
                <g stroke="url(#rnv-mono)" strokeWidth="1.2" fill="none" opacity="0.9">
                  {Array.from({ length: 14 }).map((_, i) => {
                    const y = 30 + i * 15;
                    const x1 = 60 + Math.sin(i * 0.9) * 22;
                    const x2 = 140 - Math.sin(i * 0.9) * 22;
                    return <line key={i} x1={x1} y1={y} x2={x2} y2={y} />;
                  })}
                  <path d="M60,30 C110,90 90,150 60,230" />
                  <path d="M140,30 C90,90 110,150 140,230" />
                </g>
                {/* monogram */}
                <text
                  x="100"
                  y="140"
                  textAnchor="middle"
                  fontFamily="'Cormorant Garamond', 'Playfair Display', Georgia, serif"
                  fontSize="72"
                  fill="url(#rnv-mono)"
                  fontWeight="300"
                  letterSpacing="4"
                >
                  R
                </text>
                <text
                  x="100"
                  y="176"
                  textAnchor="middle"
                  fill="#C8A96A"
                  fontFamily="Helvetica, Arial, sans-serif"
                  fontSize="8"
                  letterSpacing="4"
                >
                  RENOVA
                </text>
                <text
                  x="100"
                  y="192"
                  textAnchor="middle"
                  fill="#9aa0b0"
                  fontFamily="Helvetica, Arial, sans-serif"
                  fontSize="6"
                  letterSpacing="3"
                >
                  PEPTIDE CO.
                </text>
              </svg>
            </div>
          </motion.div>
        </div>

        {/* Text block */}
        <motion.div
          style={reduce ? undefined : { y: textY }}
          className="lg:col-span-6"
        >
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-[#C8A96A]" />
            <span className="overline !text-[#D8BE87]">A refined stance</span>
          </div>
          <h2 className="font-serif text-3xl md:text-5xl text-[#F7F4EF] leading-[1.08] max-w-xl">
            A quieter kind of confidence — engineered for those who work in
            <span className="text-[#C8A96A]/90 italic"> exact terms.</span>
          </h2>
          <p className="mt-6 text-[#B9BCC4] leading-relaxed max-w-xl">
            Renova is a brand of restraint: uncluttered catalogues, honest positioning,
            careful handling. The showpiece rotating here is not a claim — it is a
            standard we hold ourselves to, batch after batch.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
